import { formatDistanceStrict, parseISO, format } from 'date-fns';
import React from 'react';
import { Checkbox } from '@mui/material';
import { LinkSimple } from '../../../styled-components/Typography';

// Helper function to format dates
const formatDate = (date) => {
  return formatDistanceStrict(date, new Date(), { addSuffix: true });
};

// Checkbox column generator
const getCheckboxColumn = (selectedJobs, onCheckboxChange, onSelectAll, jobs) => ({
  title: (
    <Checkbox
      checked={jobs.length > 0 && selectedJobs.length === jobs.length}
      indeterminate={selectedJobs.length > 0 && selectedJobs.length < jobs.length}
      onChange={onSelectAll}
    />
  ),
  field: 'checkbox',
  render: (rowData) => (
    <Checkbox
      checked={selectedJobs.includes(rowData.id)}
      onChange={() => onCheckboxChange(rowData.id)}
    />
  ),
});

// Base columns for campaigns (without checkbox)
const baseCampaignsColumns = [
  {
    title: 'NAME',
    field: 'name',
    render: (rowData) => (
      <LinkSimple
        to={{
          pathname: `/app/details/${rowData.uid}`,
        }}
        state={{
          job: rowData,
          totalItems: rowData.stats_total,
          isMenu: true,
          enableRealTimeParam: rowData.state === 'running' || rowData.state === 'initializing'
        }}
      >
        {rowData.name}
      </LinkSimple>
    )
  },
  { 
    title: 'DATA SOURCE', 
    field: 'data_source_type', 
    render: (rowData) => {
      switch (rowData.data_source_type) {
        case 'csv':
          return 'Csv';
        case 'xls':
          return 'Xls';
        case 'google.gsheets':
          return 'Google Sheets';
        case 'airtable.airtable':
          return 'Airtable';
        default:
          return rowData.data_source_type;
      }
    }
  },
  { 
    title: 'ACTION', 
    field: 'action_type', 
    render: (rowData) => {
      switch (rowData.action_type) {
        case 'gmail.send-email':
          return 'Send Email';
        case 'twilio.send-sms':
          return 'Send Sms';
        default:
          return rowData.action_type;
      }
    }
  },
  { title: 'COUNT', field: 'stats_total', align: 'right' },
  { 
    title: 'STATUS', 
    field: 'state', 
    render: (rowData) => {
      let displayText = rowData.state;
      if (rowData.state === 'completed_with_fail') {
        displayText = 'INCOMPLETED';
      }
      return <span className={`state-${rowData.state}`}>{displayText}</span>;
    }
  },
  { 
    title: 'STARTED', 
    field: 'start_date_time', 
    render: (rowData) => rowData.start_date_time 
      ? <span title={rowData.start_date_time}>{formatDate(new Date(rowData.start_date_time))}</span> 
      : null 
  },
];

// Function to get "My Campaigns" columns with checkbox
export const getMyCampaignsColumns = (selectedJobs, onCheckboxChange, onSelectAll, jobs) => [
  getCheckboxColumn(selectedJobs, onCheckboxChange, onSelectAll, jobs),
  ...baseCampaignsColumns,
];

// Function to get "Scheduled Campaigns" columns with checkbox
export const getScheduledCampaignsColumns = (selectedJobs, onCheckboxChange, onSelectAll, jobs) => [
  getCheckboxColumn(selectedJobs, onCheckboxChange, onSelectAll, jobs),
  ...baseCampaignsColumns.filter(column => column.title !== 'STARTED'),
  { 
    title: 'SCHEDULED DATE', 
    field: 'scheduled_date_time', 
    render: (rowData) => rowData.scheduled_date_time 
      ? <span title={rowData.scheduled_date_time}>{format(parseISO(rowData.scheduled_date_time), "MM-dd-yyyy HH:mm:ss")}</span> 
      : null 
  },
];

// Function to get "Past Campaigns" columns with checkbox
export const getPastCampaignsColumns = (selectedJobs, onCheckboxChange, onSelectAll, jobs) => [
  getCheckboxColumn(selectedJobs, onCheckboxChange, onSelectAll, jobs),
  ...baseCampaignsColumns,
  { 
    title: 'COMPLETED', 
    field: 'end_date_time', 
    render: (rowData) => rowData.end_date_time && rowData.state === 'completed' 
      ? <span title={rowData.end_date_time}>{formatDate(new Date(rowData.end_date_time))}</span> 
      : null 
  },
  {
    title: 'LINK TRACKING',
    field: 'link_tracking',
    render: (rowData) => rowData.link_tracking 
      ? `${rowData.link_tracking.unhover}% clicked (${rowData.link_tracking.hover} clicked)` 
      : 'No tracking',
  },
  {
    title: 'EMAIL TRACKING',
    field: 'items_opened',
    render: (rowData) => rowData.items_opened && rowData.items_sent 
      ? `${Math.round((rowData.items_opened / rowData.items_sent) * 100)}% opened (${rowData.items_opened} opened)` 
      : 'No tracking',
  },
];

// Keep legacy exports for backward compatibility if needed
export const myCampaignsColumns = baseCampaignsColumns;
export const scheduledCampaignsColumns = [
  ...baseCampaignsColumns.filter(column => column.title !== 'STARTED'),
  { 
    title: 'SCHEDULED DATE', 
    field: 'scheduled_date_time', 
    render: (rowData) => rowData.scheduled_date_time 
      ? <span title={rowData.scheduled_date_time}>{format(parseISO(rowData.scheduled_date_time), "MM-dd-yyyy HH:mm:ss")}</span> 
      : null 
  },
];
export const pastCampaignsColumns = [
  ...baseCampaignsColumns,
  { title: 'COMPLETED', field: 'end_date_time', render: (rowData) => rowData.end_date_time && rowData.state === 'completed' ? <span title={rowData.end_date_time}>{formatDate(new Date(rowData.end_date_time))}</span> : null },
  {
    title: 'LINK TRACKING',
    field: 'link_tracking',
    render: (rowData) => rowData.link_tracking ? `${rowData.link_tracking.unhover}% clicked (${rowData.link_tracking.hover} clicked)` : 'No tracking',
  },
  {
    title: 'EMAIL TRACKING',
    field: 'items_opened',
    render: (rowData) => rowData.items_opened && rowData.items_sent ? `${Math.round((rowData.items_opened / rowData.items_sent) * 100)}% opened (${rowData.items_opened} opened)` : 'No tracking',
  },
];